/**
 * YourSeatsCard — the seats on each held or booked leg, how they compare to what the owner
 * likes, and the honest state of the daily watch. Register SF2. Owner only: the API
 * answers 404 to anyone else, and this renders nothing then.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { tripsApi } from "@/lib/api";
import SeatMapDraw, { SeatLegend } from "@/components/seats/SeatMapDraw";
import type { TripSeatLeg } from "@/components/seats/seatTypes";

/** "3 hours ago". Coarse on purpose. */
export function timeAgo(iso: string, now = Date.now()): string {
  const mins = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60000));
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.round(hours / 24);
  return `${days} ${days === 1 ? "day" : "days"} ago`;
}

export function seatsWords(seats: string[]): string {
  if (seats.length === 0) return "No seats on record";
  if (seats.length === 1) return seats[0];
  return `${seats.slice(0, -1).join(", ")} and ${seats[seats.length - 1]}`;
}

const TAG_TONE: Record<NonNullable<TripSeatLeg["verdict"]>, string> = {
  first_choice: "bg-[hsl(var(--primary))]/15 text-foreground",
  acceptable: "bg-muted text-foreground",
  not_your_kind: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100",
};

export function SeatLegRow({ leg, now }: { leg: TripSeatLeg; now?: number }) {
  const [open, setOpen] = useState(false);
  const cabin = leg.map ? leg.map.layout.cabins.find((c) => leg.seats.some((s) => leg.map?.seats.find((x) => x.seat === s)?.cabin === c.name)) ?? null : null;
  // A good read older than a failed one still tells the truth about when it was read.
  const lastGood = leg.watch.lastCheckedAt && leg.watch.lastStatus !== "refused" && leg.watch.lastStatus !== "failed" && leg.watch.lastStatus !== "mismatch" ? leg.watch.lastCheckedAt : null;
  return (
    <li className="space-y-1.5 border-t border-border pt-3 first:border-t-0 first:pt-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{leg.label}</p>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-base font-medium text-foreground">{seatsWords(leg.seats)}</span>
        {leg.tag && leg.verdict && (
          <span className={"rounded px-1.5 py-0.5 text-xs font-medium " + TAG_TONE[leg.verdict]}>{leg.tag}</span>
        )}
      </div>
      {leg.why && <p className="text-sm text-muted-foreground">{leg.why}</p>}
      <p className="text-sm text-foreground">
        {leg.watch.text}
        {leg.watch.state === "watching" && lastGood ? ` Last read ${timeAgo(lastGood, now)}.` : ""}
      </p>
      {leg.tradeoff && <p className="text-xs text-muted-foreground">{leg.tradeoff}</p>}
      {leg.map && cabin && (
        <div>
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className="inline-flex min-h-11 items-center gap-1 text-sm font-medium text-[hsl(var(--primary))] hover:opacity-80"
          >
            See the seats
            <ChevronDown className={"h-4 w-4 transition-transform motion-reduce:transition-none " + (open ? "rotate-180" : "")} aria-hidden="true" />
          </button>
          {open && (
            <div className="space-y-2 pt-1">
              <SeatMapDraw cabin={cabin} seats={leg.map.seats} selected={leg.seats} />
              <SeatLegend />
              <p className="text-xs text-muted-foreground">American's map, read {timeAgo(leg.map.readAt, now)}. A check mark is your seat.</p>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export default function YourSeatsCard({ tripId }: { tripId: string }) {
  const { data } = useQuery({
    queryKey: ["tripSeats", tripId],
    queryFn: () => tripsApi.seats(tripId),
    retry: false,
  });
  if (!data || data.legs.length === 0) return null;
  return (
    <section aria-label="Your seats" className="rounded-xl border bg-card px-4 py-3 shadow-paper">
      <h2 className="mb-3 text-base font-medium text-foreground">Your seats</h2>
      <ul className="space-y-3">
        {data.legs.map((leg) => <SeatLegRow key={leg.routeId} leg={leg} />)}
      </ul>
    </section>
  );
}
